import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import * as styles from '../SideBySideVideo.module.scss'

import { useMediaQuery } from 'hooks/useMediaQuery'

import { BaseVideoConfig, CampaignConfig } from '../config'
import { TrackFn } from '../SideBySideVideoPage'
import StepHeader from './StepHeader'
import VideoThumb from './VideoThumb'
import { CameraIcon, PlayIcon, PauseIcon, UploadIcon } from 'lucide-react'

type Props = {
  config: CampaignConfig
  selected: BaseVideoConfig
  onBack: () => void
  onComplete: (userVideo: Blob) => void
  onEvent: TrackFn
}

type DesktopState = 'idle' | 'ready' | 'recording' | 'recorded'

const pickMimeType = (): string => {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]
  for (const c of candidates) {
    if (
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported(c)
    ) {
      return c
    }
  }
  return ''
}

const PREVIEW_VOLUME = 0.3

const SelectedVideoCard = ({
  selected,
  layout,
}: {
  selected: BaseVideoConfig
  layout: CampaignConfig['layout']
}) => {
  const { t } = useTranslation('sideBySideVideo')
  const [playing, setPlaying] = useState(false)

  // Orientation is fixed by the layout (the slot the base video fills in the
  // composite), so derive it directly: side-by-side fills portrait halves
  // (vertical preview), stacked fills landscape halves (horizontal, full-width
  // with meta below). Instant, no detection flash.
  const horizontal = layout === 'stacked'

  return (
    <div
      className={[styles.selectedVideo, horizontal ? styles.selectedVideoHorizontal : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.selectedThumb}>
        {playing ? (
          <video
            src={selected.src}
            crossOrigin="anonymous"
            autoPlay
            playsInline
            onLoadedMetadata={e => {
              e.currentTarget.volume = PREVIEW_VOLUME
            }}
            onEnded={() => setPlaying(false)}
          />
        ) : (
          <VideoThumb src={selected.src} posterSrc={selected.thumbnailSrc} />
        )}
        <button
          type="button"
          className={styles.selectedThumbPlay}
          aria-label={
            playing ? t('media.pausePreview') : t('media.playPreview')
          }
          onClick={() => setPlaying(p => !p)}
        >
          {playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
        </button>
      </div>
      <div className={styles.selectedMeta}>
        <h3>{selected.title}</h3>
        <p>{selected.description}</p>
      </div>
    </div>
  )
}

const MAX_FILE_BYTES = 500 * 1024 * 1024 // 500 MB — beyond this OOMs in WASM/WebCodecs

const MobileUploader = ({
  copy,
  onFile,
  onEvent,
}: {
  copy: CampaignConfig['copy']
  onFile: (f: File) => void
  onEvent: TrackFn
}) => {
  const { t } = useTranslation('sideBySideVideo')
  const recordRef = useRef<HTMLInputElement | null>(null)
  const galleryRef = useRef<HTMLInputElement | null>(null)
  const [sizeError, setSizeError] = useState<string | null>(null)

  const handleFile = (f: File | undefined, source: string) => {
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setSizeError(
        t('record.fileTooLarge', {
          limit: Math.round(MAX_FILE_BYTES / (1024 * 1024)),
        }),
      )
      return
    }
    setSizeError(null)
    onEvent('step_interaction_sbsv_video_recorded', { source })
    onFile(f)
  }

  return (
    <div className={styles.actions}>
      {sizeError && <p className={styles.error}>{sizeError}</p>}
      <input
        ref={recordRef}
        type="file"
        accept="video/*"
        capture="user"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0], 'camera')}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files?.[0], 'gallery')}
      />

      <button
        type="button"
        className={styles.btn}
        onClick={() => {
          onEvent('step_interaction_sbsv_record_start', { source: 'camera' })
          recordRef.current?.click()
        }}
      >
        {copy.recordCtaRecord}
        <CameraIcon size={20} />
      </button>

      <div className={styles.or}>
        <span>{copy.recordOr}</span>
      </div>

      <button
        type="button"
        className={styles.uploadLink}
        onClick={() => {
          onEvent('step_interaction_sbsv_upload_select', { source: 'gallery' })
          galleryRef.current?.click()
        }}
      >
        {copy.recordCtaUpload}
        <UploadIcon size={20} />
      </button>
    </div>
  )
}

const DesktopRecorder = ({
  copy,
  maxSeconds,
  onComplete,
  onEvent,
}: {
  copy: CampaignConfig['copy']
  maxSeconds: number
  onComplete: (b: Blob) => void
  onEvent: TrackFn
}) => {
  const { t } = useTranslation('sideBySideVideo')
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)

  const [state, setState] = useState<DesktopState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const galleryRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    return () => {
      stopStream()
      if (timerRef.current) window.clearInterval(timerRef.current)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-attach stream to <video> when it mounts after state change
  useEffect(() => {
    if (
      (state === 'ready' || state === 'recording') &&
      videoRef.current &&
      streamRef.current &&
      videoRef.current.srcObject !== streamRef.current
    ) {
      videoRef.current.srcObject = streamRef.current
      videoRef.current.play().catch(() => {})
    }
  }, [state])

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
  }

  const requestCamera = async () => {
    setError(null)
    onEvent('step_interaction_sbsv_record_start', { source: 'camera' })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.current = stream
      setState('ready')
    } catch (e) {
      setError((e as Error).message || t('record.cameraPermissionDenied'))
    }
  }

  const startRecording = () => {
    if (!streamRef.current) {
      setError('No active camera stream')
      return
    }
    const tracks = streamRef.current.getTracks()
    const liveVideo = tracks.find(
      t => t.kind === 'video' && t.readyState === 'live',
    )
    if (!liveVideo) {
      setError('Camera video track is not live')
      // eslint-disable-next-line no-console
      console.error(
        '[video-poc] tracks:',
        tracks.map(t => ({
          kind: t.kind,
          readyState: t.readyState,
          muted: t.muted,
          enabled: t.enabled,
        })),
      )
      return
    }

    const mimeType = pickMimeType()
    if (!mimeType) {
      setError('No supported MediaRecorder mime type')
      return
    }
    // eslint-disable-next-line no-console
    console.log('[video-poc] recording with', {
      mimeType,
      tracks: tracks.length,
    })

    chunksRef.current = []
    let rec: MediaRecorder
    try {
      rec = new MediaRecorder(streamRef.current, { mimeType })
    } catch (e) {
      setError('MediaRecorder init failed: ' + (e as Error).message)
      return
    }

    rec.ondataavailable = e => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
    }
    rec.onerror = e => {
      // eslint-disable-next-line no-console
      console.error('[video-poc] MediaRecorder error', e)
      setError('Recording error: ' + ((e as ErrorEvent).message || 'unknown'))
    }
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: mimeType })
      // eslint-disable-next-line no-console
      console.log('[video-poc] recorded blob', {
        size: b.size,
        type: b.type,
        chunks: chunksRef.current.length,
      })
      if (b.size === 0) {
        setError('Recorded blob is empty')
        stopStream()
        return
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
      setBlob(b)
      const url = URL.createObjectURL(b)
      setPreviewUrl(url)
      setState('recorded')
      stopStream()
    }
    rec.start(1000)
    recorderRef.current = rec
    setElapsed(0)
    setState('recording')

    timerRef.current = window.setInterval(() => {
      setElapsed(prev => {
        const next = prev + 0.1
        if (next >= maxSeconds) {
          stopRecording()
          return maxSeconds
        }
        return next
      })
    }, 100)
  }

  const stopRecording = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
    }
  }

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setBlob(null)
    setState('idle')
  }

  return (
    <div className={styles.actions}>
      <input
        ref={galleryRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) {
            onEvent('step_interaction_sbsv_video_recorded', { source: 'gallery' })
            onComplete(f)
          }
        }}
      />

      {error && <p className={styles.error}>{error}</p>}

      {state === 'recorded' && previewUrl ? (
        <video
          className={styles.videoPreview}
          src={previewUrl}
          controls
          playsInline
          onLoadedMetadata={e => {
            const v = e.currentTarget
            // eslint-disable-next-line no-console
            console.log('[video-poc] preview metadata', {
              duration: v.duration,
              w: v.videoWidth,
              h: v.videoHeight,
            })
            // WebM from MediaRecorder lacks duration. Seek hack forces scan.
            const bad = !Number.isFinite(v.duration) || v.duration === 0
            if (bad) {
              v.currentTime = 1e9
              const onSeeked = () => {
                v.currentTime = 0
                v.removeEventListener('seeked', onSeeked)
              }
              v.addEventListener('seeked', onSeeked)
            }
          }}
        />
      ) : state !== 'idle' ? (
        <video
          ref={videoRef}
          className={styles.videoPreview}
          muted
          playsInline
          autoPlay
        />
      ) : null}

      {state === 'recording' && (
        <div className={styles.progress}>
          <div
            className={styles.progressBar}
            style={{ width: `${(elapsed / maxSeconds) * 100}%` }}
          />
        </div>
      )}

      {state === 'idle' && (
        <>
          <button type="button" className={styles.btn} onClick={requestCamera}>
            {copy.recordCtaRecord}
            <CameraIcon size={20} />
          </button>
          <div className={styles.or}>
            <span>{copy.recordOr}</span>
          </div>
          <button
            type="button"
            className={styles.uploadLink}
            onClick={() => {
              onEvent('step_interaction_sbsv_upload_select', {
                source: 'gallery',
              })
              galleryRef.current?.click()
            }}
          >
            {copy.recordCtaUpload}
            <UploadIcon size={20} />
          </button>
        </>
      )}

      {state === 'ready' && (
        <button type="button" className={styles.btn} onClick={startRecording}>
          {t('record.startRecording')}
        </button>
      )}

      {state === 'recording' && (
        <button type="button" className={styles.btn} onClick={stopRecording}>
          {t('record.stopWithTimer', {
            elapsed: elapsed.toFixed(1),
            max: maxSeconds,
          })}
        </button>
      )}

      {state === 'recorded' && blob && (
        <>
          <button
            type="button"
            className={styles.btn}
            onClick={() => {
              onEvent('step_interaction_sbsv_video_recorded', {
                source: 'camera',
              })
              onComplete(blob)
            }}
          >
            {t('record.useThisTake')}
          </button>
          <button type="button" className={styles.uploadLink} onClick={retake}>
            {t('record.retake')}
          </button>
        </>
      )}
    </div>
  )
}

const RecordStage = ({ config, selected, onBack, onComplete, onEvent }: Props) => {
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const { copy } = config
  const maxSeconds = Math.ceil(selected.durationSeconds)

  return (
    <>
      <StepHeader step={2} total={4} onBack={onBack} />

      <div style={{ width: '100%' }}>
        <h2 className={styles.title}>{copy.recordTitle}</h2>
        <p className={styles.subtitle}>{copy.recordSubtitle}</p>
      </div>

      <SelectedVideoCard selected={selected} layout={config.layout} />

      <div className={styles.divider} />

      <p className={styles.addLabel}>{copy.recordAddYourVideo}</p>

      {isDesktop ? (
        <DesktopRecorder
          copy={copy}
          maxSeconds={maxSeconds}
          onComplete={onComplete}
          onEvent={onEvent}
        />
      ) : (
        <MobileUploader copy={copy} onFile={onComplete} onEvent={onEvent} />
      )}
    </>
  )
}

export default RecordStage
